import { LoaderMessage } from '../utils/loaders/loader-message';
import { AppInjector } from '../utils/services/set-app-injector';
import DataSlotModel from './data-slot.model';
import { LoaderActions } from '../utils/loaders/loader-actions.enum';
import { IBundle, ICartDetails } from '../interfaces/cart-details.interface';
import { DeviceModel } from './device.model';
import { CartDetailsSourceIdsEnum } from './cartDetails/data-source-ids.enum';
import { DataLayerService } from '../customer-actions/data-layer.service';
import { IServerResponse } from '../interfaces/server-response.interface';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../external-interfaces/notification-provider.service';
import { ConfigService } from '../cart-process/config.service';
import { CommonModalTemplatesEnum } from '../utils/modals/common-modal-templates.enum';

enum WarningCodeNames {
  ADD_OPTIONAL_INTERNET = 'ADD_OPTIONAL_INTERNET',
  REMOVE_OPTIONAL_INTERNET = 'REMOVE_OPTIONAL_INTERNET',
  CUSTOM_MESSAGE = 'CUSTOM_MESSAGE',
  DELETE_DEVICE = 'DELETE_DEVICE'
}

enum OptionalInternetLoaderMessages {
  ADD = 'Dodajemy ofertę internetu domowego do Twojego koszyka',
  DELETE = 'Usuwamy ofertę internetu domowego z Twojego koszyka',
  CHANGE_DEVICE = 'Nastąpi przekierowanie na stronę z urządzeniami',
  DELETE_DEVICE = 'Usuwamy urządzenie'
}

export default class DataSlotFirstPageModel extends DataSlotModel {
  public changeDeviceUrl: string;
  public ifNoOffer: boolean;
  public showOfferHeader: boolean;
  public ifOfferHasNoDevice: boolean;
  public ifOfferHasDevice: boolean;
  public isWarningVisible = false;
  public isObligatory = false;
  public warningMessage = '';
  public ifOfferHasGadget: boolean;
  public loader: LoaderMessage;
  public device: DeviceModel;
  public isTvOffer = false;
  public keepGadgets: string;

  private endpoint: any = AppInjector.get('Endpoint');
  private notificationProvider: NotificationProviderService = AppInjector.get(NotificationProviderService);
  private dataLayerService: DataLayerService = AppInjector.get(DataLayerService);

  constructor(
    bundle: IBundle,
    ordinal: number,
    isInCart: boolean,
    title: string,
  ) {
    super(bundle, ordinal, isInCart, title);

    this.ifOfferHasNoDevice = isInCart && !this.hasDevice;
    this.ifOfferHasDevice = isInCart && this.hasDevice;
    this.ifOfferHasGadget = isInCart && this.hasGadget;
    this.isObligatory = isInCart && bundle.obligatory;
    this.ifNoOffer = !isInCart;
    this.showOfferHeader = isInCart;
    this.isTvOffer = this.isTrafficTypeTV();
    this.keepGadgets = this.ifOfferHasGadget ? `&keepGadgets=${this.ifOfferHasGadget}`: '';

    if (this.bundleMessages.length) {
      this.showWarningMessage(WarningCodeNames.CUSTOM_MESSAGE);
    }

    this.loader = new LoaderMessage(
      LoaderActions.OPTIONAL_INTERNET,
      [LoaderActions.SUMMARY_PRICES_LOADER]
    );

    this.notificationProvider.subscribe(
      NotificationProviderTypeEnum.CONFIG,
      (config: ConfigService): void => {
        if (!config) return;

        this.changeDeviceUrl = `${config.changeDeviceUrl}?bundleNo=${this.bundleNumber}&oid=${bundle.offer.handsetOfferIdForChangeDevice}&mode=router${this.keepGadgets}`;
      }
    );
  }

  private forwardingToPortal(url: string = ''): void {
    if (!url.length) {
      return;
    }

    window.location.href = url;
  }

  private propagateCartDetails(serverResponse: IServerResponse, additionalId?: CartDetailsSourceIdsEnum): void {
    if (serverResponse.status.code.code === 0) {
      const cartDetails: ICartDetails = serverResponse.response.cartDetails;

      if (additionalId) {
        cartDetails._sourceId = additionalId;
        cartDetails._sourceToken = this.dataLayerService.getToken(additionalId);
      }

      this.notificationProvider.publish(
        NotificationProviderTypeEnum.CART_DETAILS,
        cartDetails
      );
    }
  }

  private showWarningMessage(code: WarningCodeNames): void {
    this.isWarningVisible = true;

    if (code === WarningCodeNames.ADD_OPTIONAL_INTERNET) {
      this.warningMessage = 'Nie udało się dodać oferty internetu';
    } else if (code === WarningCodeNames.REMOVE_OPTIONAL_INTERNET) {
      this.warningMessage = 'Nie udało się usunąć oferty internetu';
    } else if (code === WarningCodeNames.DELETE_DEVICE) {
      this.warningMessage = 'Nie udało się usunąć urządzenia';
    } else if (code === WarningCodeNames.CUSTOM_MESSAGE) {
      // Wybrane urządzenie nie jest już dostępne
      this.warningMessage = this.bundleMessages.join(' ').replace(/(<([^>]+)>)/ig, '');
    }
  }

  public chooseOptionalInternet(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.loader.showLoader || !this.ifNoOffer) {
      return;
    }

    this.loader.setLoaderMessage(OptionalInternetLoaderMessages.ADD);
    this.loader.start();

    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.OPTIONAL_INTERNET);

    this.dataLayerService.onAddOptionalInternetSlot();

    this.endpoint('cart.addOptionalInternet')
      .run()
      .then((response: IServerResponse) => {
        if (response.status.code.code !== 0) {
          this.showWarningMessage(WarningCodeNames.ADD_OPTIONAL_INTERNET);
        } else {
          this.propagateCartDetails(response, CartDetailsSourceIdsEnum.OPTIONAL_INTERNET);
        }
      })
      .catch(() => {
        this.showWarningMessage(WarningCodeNames.ADD_OPTIONAL_INTERNET);
      })
      .finally(() => {
        this.loader.stop();
      });
  }

  public deleteOptionalInternet(event: Event): void {
    event.preventDefault();

    if (this.loader.showLoader) {
      return;
    }

    this.loader.setLoaderMessage(OptionalInternetLoaderMessages.DELETE);
    this.loader.start();

    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.REMOVE_SLOT);

    this.dataLayerService.onRemoveOptionalInternetSlot(this.uid);

    this.endpoint('cart.removeOptionalInternet')
      .requestData({
        bundleNo: this.bundleNumber
      })
      .run()
      .then((serverResponse: IServerResponse) => {
        if (serverResponse.status.code.code !== 0) {
          this.showWarningMessage(WarningCodeNames.REMOVE_OPTIONAL_INTERNET);
        } else {
          this.propagateCartDetails(serverResponse, CartDetailsSourceIdsEnum.REMOVE_SLOT);
        }
      })
      .catch(() => {
        this.showWarningMessage(WarningCodeNames.REMOVE_OPTIONAL_INTERNET);
      })
      .finally(() => {
        this.loader.stop();
      });
  }

  public chooseDevice(event: Event): void {
    event.preventDefault();

    if (this.loader.showLoader) {
      return;
    }

    this.loader.setLoaderMessage(OptionalInternetLoaderMessages.ADD);
    this.loader.start();

    this.forwardingToPortal(this.changeDeviceUrl);
  }

  public changeDevice(event: Event): void {
    event.preventDefault();

    if (this.loader.showLoader) {
      return;
    }

    this.loader.setLoaderMessage(OptionalInternetLoaderMessages.CHANGE_DEVICE);
    this.loader.start();

    this.forwardingToPortal(this.changeDeviceUrl);
  }

  public deleteDevice(event: Event): void {
    event.preventDefault();

    if (this.loader.showLoader) {
      return;
    } else if (this.ifOfferHasGadget) {
      this.deleteDataSlotDeviceModal();
      return;
    }

    this.loader.setLoaderMessage(OptionalInternetLoaderMessages.DELETE_DEVICE);
    this.loader.start();

    this.changeDeviceEndpoint();
  }

  private deleteDataSlotDeviceModal(): void {
    const modalData = {
      templateName: CommonModalTemplatesEnum.REMOVE_SET_CONFIRMATION_MODAL,
      gadgetsList: this._computedFields.gadgetsList,
      deviceItem: this.device,
      remove: (event: Event): void => {
        event.preventDefault();

        this.loader.setLoaderMessage(OptionalInternetLoaderMessages.DELETE_DEVICE);
        this.loader.start();
        this.changeDeviceEndpoint();
      }
    };

    this.commonModalService.setData(modalData);
  }

  private changeDeviceEndpoint(): void {
    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.REMOVE_DEVICE);

    this.dataLayerService.onRemoveOptionalInternetSlotDevice(this.uid);

    this.endpoint('newCart.changeDevice', {
      offerId: this.offerId,
      bundleNo: this.bundleNumber
    })
      .queue()
      .run()
      .then((response: any) => {
        if (response === 'OK') {
          this.propagateCartDetails(response, CartDetailsSourceIdsEnum.REMOVE_DEVICE);
        } else {
          this.showWarningMessage(WarningCodeNames.DELETE_DEVICE);
        }
      })
      .catch(() => {
        this.showWarningMessage(WarningCodeNames.DELETE_DEVICE);
      })
      .finally(() => {
        this.loader.stop();
      });
  }
}
